/**
 * Pricing Intelligence — Frontend Types
 *
 * Mirrors the zeecore domain types. Kept separate to avoid coupling the
 * frontend build to the backend module.
 */

export type PricingHorizon = 'daily' | 'weekly' | 'monthly' | 'quarterly';

export type RecommendedAction = 'increase' | 'decrease' | 'hold' | 'review';

export type BriefCategory = 'increase' | 'decrease' | 'hold' | 'monitor';

export type SheetStatus = 'draft' | 'reviewed' | 'approved';

export type DepotRegion = 'Duxford' | 'Bedford' | 'St Ives' | 'Unmapped';

export type VehicleType =
  | 'Lorry'
  | 'Tractor Unit'
  | 'Rigid Truck'
  | 'Mini Truck'
  | 'Service Van';

export const ALL_REGIONS: DepotRegion[] = ['Duxford', 'Bedford', 'St Ives', 'Unmapped'];

export const ALL_VEHICLE_CLASSES: VehicleType[] = [
  'Lorry',
  'Tractor Unit',
  'Rigid Truck',
  'Mini Truck',
  'Service Van',
];

export interface DataCompleteness {
  gps: number;
  orders: number;
  fuel: number;
  drivers: number;
}

export interface ConfidenceDriver {
  signal: string;
  weight: number;
  contribution: number;
  source: 'observed' | 'declared' | 'inferred' | 'assumed';
  note?: string;
}

export interface BucketSignalSet {
  region: DepotRegion;
  vehicleClass: VehicleType;
  demandPressure: number;
  capacityPressure: number;
  idleRisk: number;
  repositionPenalty: number;
  positionValue: number;
  strategicAdjustment: number;
  realisationSignal: number;
  marginSignal: number | null;
  dataCompleteness: DataCompleteness;
  recommendationConfidence: number;
  confidenceDrivers: ConfidenceDriver[];
}

export interface PricingRecommendation {
  recommendationId: string;
  regionId: DepotRegion;
  vehicleClass: VehicleType;
  recommendedAction: RecommendedAction;
  recommendedAdjustmentPct: number;
  compositeScore: number;
  confidence: number;
  reviewRequired: boolean;
  reasoning: string[];
  signals: BucketSignalSet;
  notes?: string;

  // Manager-facing fields
  briefCategory: BriefCategory;
  managerHeadline: string;
  adjustmentRange: string;
  whyNow: string;
  whatThisMeans: string;
}

export interface PricingSheetSummary {
  executiveSummary: string;
  notableRisks: string[];
  notableOpportunities: string[];
  suggestedActions: string[];
  confidenceCaveats: string[];
  globalDataNotes: string[];
  brief: ManagerBrief;
  coverage: SheetCoverage;
}

export interface ManagerBriefItem {
  region: DepotRegion;
  vehicleClass: VehicleType;
  headline: string;
  adjustmentRange: string;
  whyNow: string;
  whatThisMeans: string;
  confidence: number;
  reviewRequired: boolean;
  actionConfidence?: number;
  dataConfidence?: number;
}

export interface ManagerBriefSection {
  title: string;
  items: ManagerBriefItem[];
}

export interface ManagerBrief {
  narrative: string;
  increase: ManagerBriefSection;
  decrease: ManagerBriefSection;
  hold: ManagerBriefSection;
  monitor: ManagerBriefSection;
}

export interface SheetCoverage {
  totalBuckets: number;
  mappedBuckets: number;
  reviewOnlyBuckets: number;
  sparseMarginBuckets: number;
  averageConfidence: number;
  lowConfidenceSheet: boolean;
}

export interface PricingSheet {
  sheetId: string;
  horizon: PricingHorizon;
  generatedAt: string;
  effectiveFrom: string;
  effectiveTo: string;
  scope: {
    regions: DepotRegion[];
    vehicleClasses: VehicleType[];
  };
  summary: PricingSheetSummary;
  recommendations: PricingRecommendation[];
  status: SheetStatus;
  metadata: {
    generationDurationMs: number;
    inputDataRange: { from: string; to: string };
    vehicleDaysAnalysed: number;
    ordersAnalysed: number;
  };
}

export interface GenerateSheetParams {
  horizon: PricingHorizon;
  effectiveFrom?: string;
  regions?: DepotRegion[];
  vehicleClasses?: VehicleType[];
}
